import { Outlet } from 'react-router-dom';
import { FronteggProvider, FronteggThemeOptions, useAuth } from '@frontegg/react';
import AuthService from './services/auth.service';

const authService = AuthService.getInstance();

const contextOptions = {
  baseUrl: authService.authUrl,
  clientId: authService.feClientId,
  appId: authService.feAppId,
};

const authOptions = {
  keepSessionAlive: true,
  routes: {
    loginUrl: (authService.useExternalLogin || authService.isCustomDomain) ? '/ext-login' : '/login',
    logoutUrl: '/do/logout',
    authenticatedUrl: '/',
  },
};

const themeOptions: FronteggThemeOptions = {
  loginBox: {},
};

// Wrapper component that uses useAuth hook inside FronteggProvider context
function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();

  // If this app is configured for external login (local dev), redirect
  // authenticated users to the legacy app immediately before rendering any UI
  if (authService.useExternalLogin && isAuthenticated) {
    window.location.href = authService.authAppUrl;
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return <Outlet />;
}

// On custom domains with SSO: skip Frontegg and redirect to IdP unless
// we're returning from SSO (indicated by #sso-authenticated hash)
const skipSsoRedirect = window.location.hash === '#sso-authenticated';
if (skipSsoRedirect) {
  // Clean up the hash so it doesn't stick around
  history.replaceState(null, '', window.location.pathname + window.location.search);
}

function App() {
  if (authService.isCustomDomain && authService.ssoLoginUrl && !skipSsoRedirect) {
    const returnTo = encodeURIComponent(window.location.origin + '/#sso-authenticated');
    const returnToken = encodeURIComponent(authService.returnToken || '');
    const idpUrl = encodeURIComponent(authService.ssoLoginUrl);
    window.location.href = `${authService.authAppUrl}/sso-bounce?idpUrl=${idpUrl}&returnTo=${returnTo}&returnToken=${returnToken}`;
    return null;
  }

  return (
    <FronteggProvider
      authOptions={authOptions}
      contextOptions={contextOptions}
      themeOptions={themeOptions}
      hostedLoginBox={false}
      entitlementsOptions={{ enabled: true }}
    >
      <AppContent />
    </FronteggProvider>
  );
}

export default App;
